import Emitter from '@resources/js/helpers/Emitter'
import FlatpickrAdapter from '@resources/js/components/form/calendar/FlatpickrAdapter'
import Dropdown from '@resources/js/ui/Dropdown'
import Guests from './Guests'

export default class FormView extends Emitter {
	#formElement
	#dateFromElement
	#dateToElement
	#submitBtnElement
	#calendarAdapter
	#calendarConfig
	#calendar
	#calendarElement
	#guestsTogglerElement // тригер для відкриття dropdown гостей
	#guestDropdownElement // dropdown елемент вибору гостей
	#guestsElement // елемент-контейнер гостів
	#guestsInstance // екземпляр класу Guests
	#guestsCountElement // елемент відображення загальної кількості гостей
	#guestsLabelElement // елемент відображення вокабу для гостей (однина/множина). Повинно бути 2 дата атрибута: singular і plural

	constructor(
		domElementOrSelector,
		{ calendarAdapter = FlatpickrAdapter, calendarConfig = {} } = {}
	) {
		super()
		this.#formElement =
			domElementOrSelector instanceof HTMLElement
				? domElementOrSelector
				: document.querySelector(domElementOrSelector)

		if (!this.#formElement) throw new Error('Form element is not found!')

		this.#dateFromElement = this.#formElement.querySelector('.js-form-from')
		this.#dateToElement = this.#formElement.querySelector('.js-form-to')

		this.#calendarElement = this.#formElement.querySelector(
			'.js-calendar-element'
		)

		this.#guestsTogglerElement =
			this.#formElement.querySelector('.js-guests-toggler')
		this.#guestDropdownElement =
			this.#formElement.querySelector('.js-guests-dropdown')
		this.#guestsElement = this.#formElement.querySelector('.js-guests')
		this.#guestsCountElement =
			this.#formElement.querySelector('.js-guests-count')
		this.#guestsLabelElement =
			this.#formElement.querySelector('.js-guests-label')

		this.#submitBtnElement = this.#formElement.querySelector('.js-form-btn')

		this.#calendarAdapter = calendarAdapter
		this.#calendarConfig = calendarConfig
		this.#calendar = null

		this.#init()
	}

	#init() {
		this.#initCalendar()
		this.#initGuestsDropdown()
		this.#initGuests()
		this.#initEvents()
	}

	#initEvents() {
		this.#formElement.addEventListener('click', e => {
			if (e.target.closest('.js-calendar-trigger')) this.toggleCalendar()
		})

		this.#submitBtnElement.addEventListener(
			'click',
			this.#handleSubmit.bind(this)
		)

		if (!this.#guestsElement) return

		this.#guestsInstance.on('adults-change', value => {
			this.emit('adults-change', value)
		})

		this.#guestsInstance.on('children-change', value => {
			this.emit('children-change', value)
		})

		this.#guestsInstance.on('child-age-change', data => {
			this.emit('child-age-change', data)
		})

		this.#guestsInstance.on('guests-count-change', guestCountsObj => {
			const totalGuestCount = guestCountsObj.adults + guestCountsObj.children

			this.#setTotalGuestsCount(totalGuestCount)
			this.#setGuestsLabel(totalGuestCount)
		})
	}

	#initCalendar() {
		this.#calendar = new this.#calendarAdapter(
			this.#calendarElement,
			this.#calendarConfig
		)

		this.#calendar.on('change', selectedDates => {
			const [checkIn, checkOut] = selectedDates
			this.emit('dates-changed', { checkIn, checkOut })
		})
	}

	#initGuests() {
		if (!this.#guestsElement) return

		this.#guestsInstance = new Guests(this.#guestsElement)
	}

	#initGuestsDropdown() {
		if (!this.#guestsTogglerElement || !this.#guestDropdownElement) return

		new Dropdown(this.#guestsTogglerElement, {
			target: this.#guestDropdownElement,
			lockScroll: false,
		})
	}

	openCalendar() {
		this.#calendar.open()
	}

	closeCalendar() {
		this.#calendar.close()
	}

	toggleCalendar() {
		this.#calendar.toggle()
	}

	setCalendarDates(datesArray) {
		this.#calendar.setDates(datesArray)
	}

	#handleSubmit() {
		this.emit('form-submit')
	}

	update(state) {
		const { checkIn, checkOut, adultsCount, childrenCount } = state

		if (checkIn) this.setCheckIn(checkIn)
		if (checkOut) this.setCheckOut(checkOut)

		if (!this.#guestsInstance) return

		// Перевіряємо, що count не null і не undefined (0 вважається валідним)
		if (adultsCount != null) this.#guestsInstance.setAdultsCount(adultsCount)
		if (childrenCount != null)
			this.#guestsInstance.setChildrenCount(childrenCount)
	}

	setCheckIn(elementDateString) {
		this.#dateFromElement.textContent = elementDateString
	}

	setCheckOut(elementDateString) {
		this.#dateToElement.textContent = elementDateString
	}

	#setTotalGuestsCount(count) {
		if (!this.#guestsCountElement) return
		this.#guestsCountElement.textContent = count
	}

	#setGuestsLabel(totalGuests) {
		if (!this.#guestsLabelElement) return

		const { singular, plural } = this.#guestsLabelElement.dataset
		if (!singular || !plural) return

		this.#guestsLabelElement.textContent = totalGuests > 1 ? plural : singular
	}

	updateChildrenAgeSelects(count) {
		this.#guestsInstance?.updateChildAgeSelects(count)
	}

	setChildAgeValue(index, value) {
		this.#guestsInstance?.setChildAge(index, value)
	}
}
